import { Directive, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { PostalAddress } from '../../../common/model/postal-address';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { CommandHandlerResolver } from '../../../core/service/command/command-handler-resolver.service';
import { Fragment } from '../../../core/model/fragment';
import { ComponentMessageDispatcher } from '../../../core/service/component-message-dispather.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { mailCommand } from '../../../message/model/const/command';
import { leaseAgreementCommand } from '../../command/lease-agreement.command';
import { LeaseRow } from '../../model/lease-row';
import { Tenant } from '../../model/tenant';
import { VacantRentalObject } from '../../model/vacant-rental-object';
import { NoticePeriodComponent } from '../notice-period/notice-period.component';
import { DataService } from './../../../core/service/base/data.service';
import {
  ColdWaterPaymentKind,
  ColdWaterPaymentKindToString,
  HeatingPaymentKind,
  HeatingPaymentKindToString,
  HotWaterPaymentKind,
  HotWaterPaymentKindToString,
  HouseholdElectricityPaymentKind,
  HouseholdElectricityPaymentKindToString,
  LeaseAgreementCategory,
  OrganizationOfTenantsKind,
  ParkingElectricityPaymentKind,
  ParkingElectricityPaymentKindToString,
  RentalObjectCategory,
  RentPaymentKind,
  RentPaymentKindToString,
} from './../../model/const/enums';
import { Contract } from './../../model/contract';
import { ColumnDefinition, ColumnVisibility } from '../../../core/model/column-definition';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { FaultNotification } from '../../../fault-notification/model/faultNotification';
import { LocalDate } from '../../../core/model/local-date';
import { UserAccountService } from '../../../common/service/user-account.service';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { Money } from '../../../core/model/money';
import { LeaseAgreementRentalObject } from '../../model/lease-agreement-rental-object';
import { ColumnDefinitionService } from '../../service/data/column-definition.service';
import { LeaseAgreementLogService } from '../../service/data/lease-agreement-log.service';
import { LogRecord } from '../../../common/model/log-record';
import { CommandBase, standardCommand } from '../../../core/model/command';
import { NegotiationComponent } from '../negotiation/negotiation.component';
import { LeaseAgreementImdConsumption } from '../../model/lease-agreement-imd-consumption';
import { LeaseAgreementImdConsumptionService } from '../../service/data/lease-agreement-imd-consumption.service';
import { LeaseAgreementTemplate } from '../../model/lease-agreement-template';
import { TaxTypeCode } from '../../model/tax-type-code';
import { LeaseAgreementDocumentManagerComponent } from '../lease-agreement-document-manager/lease-agreement-document-manager.component';
import { Article } from '../../model/article';
import { SelectionItem } from '../../../core/component/select/select.component';
import { toPropertyTaxPercentageDisplayValue } from '../../helper/property-tax.helpers';
import { LanguageService } from '../../../core/service/language.service';

const formModelNames = {
  invoiceAddress: 'invoiceAddress',
  invoicePeriod: 'invoicePeriod',
  leaseAgreementNumber: 'leaseAgreementNumber',
  leaseAgreementRows: 'leaseAgreementRows',
  originalContractualRent: 'originalContractualRent',
  rentalObjects: 'rentalObjects',
  rentalObjectNumber: 'rentalObjectNumber',
  tenantOneName: 'tenantOneName',
  tenantTwoName: 'tenantTwoName',
  usage: 'usage',
};

@Directive()
export abstract class ContractFormComponentBase<TModel extends Contract> extends FormComponentBase<TModel> {
  public readonly documentsFragment = Fragment.documentsTab;
  constructor(
    protected messageDispatcher: ComponentMessageDispatcher,
    private _cdf: ColumnDefinitionFactory,
    protected fb: FormBuilder,
    protected fcf: FormControlExFactory,
    private _userAccount: UserAccountService,
    private _columnDefinitionService: ColumnDefinitionService,
    activatedRoute: ActivatedRoute,
    service: DataService<TModel>,
    messageService: MessageService,
    navigation: NavigationService,
    commandDispatcher: CommandDispatcher,
    handlerResolver: CommandHandlerResolver,
    private logService: LeaseAgreementLogService,
    private leaseAgreementImdConsumptionService: LeaseAgreementImdConsumptionService,
    protected languageService: LanguageService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);

    this.AddSubscription(
      this._userAccount.current.subscribe({
        next: account => {
          this.addDeleteMenu = false;
          if (account.hasAccess(ModuleNames.Messages, Features.ManageESignatures) && account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('COMMON.COMMAND.SEND_FOR_DIGITAL_SIGNING', leaseAgreementCommand.sendForEsignDocument);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.APPROVE', leaseAgreementCommand.approve);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_APPROVAL', leaseAgreementCommand.withdrawApproval);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.TERMINATE', leaseAgreementCommand.terminate);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.WITHDRAW_TERMINATION', leaseAgreementCommand.withdrawTermination);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.PRINT_TERMINATION_FORM', leaseAgreementCommand.printTerminationForm);
          if (account.hasAccess(ModuleNames.Messages, Features.ManageEmail)) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_EMAIL', mailCommand.sendTenantEmail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageMail)) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_LETTER', mailCommand.sendTenantMail);
          }
          if (account.hasAccess(ModuleNames.Messages, Features.ManageSms)) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SEND_SMS', mailCommand.sendTenantSMS);
          }
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.CREATE_INVOICE', leaseAgreementCommand.createInvoice);
          }
          this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', leaseAgreementCommand.showInvoices);
          if (account.hasLeaseAgreementWriteAccess()) {
            this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', leaseAgreementCommand.linkDocument);
          }
        },
      })
    );
    this.faultNotificationColumnDefinition = this._columnDefinitionService.create('LeaseAgreementfaultNotification');
    this.otherLeaseAgreementColumnDefinition = this._columnDefinitionService.create('OtherLeaseAgreementsColumnDefinition');
    this.logColumnDefinition = this._columnDefinitionService.create('Log');
    this.imdConsumptionColumnDefinition = this._columnDefinitionService.create('LeaseAgreementImdConsumption');
  }

  @ViewChild(NoticePeriodComponent) noticePeriodComponent: NoticePeriodComponent;
  @ViewChild(NegotiationComponent) negotiationComponent: NegotiationComponent;
  @ViewChild(LeaseAgreementDocumentManagerComponent) leaseAgreementDocumentManager: LeaseAgreementDocumentManagerComponent;

  faultNotificationColumnDefinition: ColumnDefinition[];
  otherLeaseAgreementColumnDefinition: ColumnDefinition[];
  logColumnDefinition: ColumnDefinition[];
  imdConsumptionColumnDefinition: ColumnDefinition[];

  fromDate: LocalDate;
  rentalObjectSurface: number;
  invoiceAddress: PostalAddress;
  private _isTotalRent: boolean;
  get isTotalRent(): boolean {
    return this._isTotalRent;
  }
  private _isRentPaymentCustom: boolean;
  get isRentPaymentCustom(): boolean {
    return this._isRentPaymentCustom;
  }

  get isRentPaymentCustomOrTotal(): boolean {
    return this._isRentPaymentCustom || this._isTotalRent;
  }

  heatingPayments = [
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IncludedInTheRent, this.isBrf()), value: HeatingPaymentKind.IncludedInTheRent },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.IndividualMeasurement), value: HeatingPaymentKind.IndividualMeasurement },
    { name: HeatingPaymentKindToString(HeatingPaymentKind.CostIsAdded), value: HeatingPaymentKind.CostIsAdded },
    {
      name: HeatingPaymentKindToString(HeatingPaymentKind.SeparateSubscription, this.isBrf()),
      value: HeatingPaymentKind.SeparateSubscription,
    },
  ];
  coldWaterPayments = [
    {
      name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IncludedInTheRent, this.isBrf()),
      value: ColdWaterPaymentKind.IncludedInTheRent,
    },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.IndividualMeasurement), value: ColdWaterPaymentKind.IndividualMeasurement },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.AccordingToTemplate), value: ColdWaterPaymentKind.AccordingToTemplate },
    { name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.CostIsAdded), value: ColdWaterPaymentKind.CostIsAdded },
    {
      name: ColdWaterPaymentKindToString(ColdWaterPaymentKind.SeparateSubscription, this.isBrf()),
      value: ColdWaterPaymentKind.SeparateSubscription,
    },
  ];
  hotWaterPayments = [
    {
      name: HotWaterPaymentKindToString(HotWaterPaymentKind.IncludedInTheRent, this.isBrf()),
      value: HotWaterPaymentKind.IncludedInTheRent,
    },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.IndividualMeasurement), value: HotWaterPaymentKind.IndividualMeasurement },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.AccordingToTemplate), value: HotWaterPaymentKind.AccordingToTemplate },
    { name: HotWaterPaymentKindToString(HotWaterPaymentKind.CostIsAdded), value: HotWaterPaymentKind.CostIsAdded },
    {
      name: HotWaterPaymentKindToString(HotWaterPaymentKind.SeparateSubscription, this.isBrf()),
      value: HotWaterPaymentKind.SeparateSubscription,
    },
  ];
  householdElectricityPayments = [
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IncludedInTheRent, this.isBrf()),
      value: HouseholdElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.IndividualMeasurement),
      value: HouseholdElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.SeparateSubscription, this.isBrf()),
      value: HouseholdElectricityPaymentKind.SeparateSubscription,
    },
    {
      name: HouseholdElectricityPaymentKindToString(HouseholdElectricityPaymentKind.AccordingToTemplate),
      value: HouseholdElectricityPaymentKind.AccordingToTemplate,
    },
  ];
  parkingElectricityPayments = [
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IncludedInTheRent, this.isBrf()),
      value: ParkingElectricityPaymentKind.IncludedInTheRent,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.IndividualMeasurement),
      value: ParkingElectricityPaymentKind.IndividualMeasurement,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption),
      value: ParkingElectricityPaymentKind.PartOfTheCommonElectricityConsumption,
    },
    {
      name: ParkingElectricityPaymentKindToString(ParkingElectricityPaymentKind.SpecifiedInSpecificTerms),
      value: ParkingElectricityPaymentKind.SpecifiedInSpecificTerms,
    },
  ];
  rentPayments = [
    { name: RentPaymentKindToString(RentPaymentKind.TotalRent, this.isBrf()), value: RentPaymentKind.TotalRent },
    { name: RentPaymentKindToString(RentPaymentKind.SpecifiedAdditions), value: RentPaymentKind.SpecifiedAdditions },
    { name: RentPaymentKindToString(RentPaymentKind.OwnTemplate), value: RentPaymentKind.OwnTemplate },
  ];
  leaseAgreementRows: LeaseRow[];
  tenantOne: Tenant;
  representativeFilter: string;

  protected onMessageRecieved(message: any) {
    if (message instanceof VacantRentalObject) {
      this.messageDispatcher.messageDelivered();
      this.form.controls['rentalObject'].setValue(message);
      this.form.controls['rentalObject'].disable();
      this.onRentalObjectChanged(message);
    }
  }

  protected executedWithResult(result: boolean, command: CommandBase): void {
    super.executedWithResult(result, command);

    if (!result) {
      return;
    }

    if (command.id === leaseAgreementCommand.linkDocument && this.leaseAgreementDocumentManager) {
      this.leaseAgreementDocumentManager.refresh();
    }

    if (
      command.id === leaseAgreementCommand.withdrawTermination ||
      command.id === leaseAgreementCommand.approve ||
      command.id === leaseAgreementCommand.withdrawApproval
    ) {
      this.navigation.reloadCurrentRoute();
    }
  }

  private _updateRentFields(payment: RentPaymentKind): void {
    this._isTotalRent = payment === RentPaymentKind.TotalRent;
    if (this._isTotalRent) {
      this.form.controls['heatingPayment'].setValue(HeatingPaymentKind.IncludedInTheRent);
      this.form.controls['coldWaterPayment'].setValue(ColdWaterPaymentKind.IncludedInTheRent);
      this.form.controls['hotWaterPayment'].setValue(HotWaterPaymentKind.IncludedInTheRent);
      this.form.controls['householdElectricityPayment'].setValue(HouseholdElectricityPaymentKind.IncludedInTheRent);
      this.form.controls['parkingElectricityPayment'].setValue(ParkingElectricityPaymentKind.IncludedInTheRent);
      this.form.controls['parkingChargingElectricityPayment'].setValue(ParkingElectricityPaymentKind.IncludedInTheRent);
    }

    this._isRentPaymentCustom = payment === RentPaymentKind.OwnTemplate;
  }

  protected getFormGroup(): FormGroup {
    let form = this.fb.group({
      agreementDate: this.fb.group({}),
      giveUpSecurityOfTenureFrom: this.fb.group({}),
      giveUpSecurityOfTenureTo: this.fb.group({}),
      fromDate: this.fb.group({}),
      invoiceAddress: this.fb.group({}),
      status: this.fcf.createHidden(this.dataContext.status),
      rentPayment: this.fcf.createMandatory(
        this.isBrf() ? 'LEASE_AGREEMENT.RENT_PAYMENT_BRF' : 'LEASE_AGREEMENT.RENT_PAYMENT',
        this.dataContext.rentPayment,
        this.isBrf() ? 'LEASE_AGREEMENT.HELP_TEXT.RENT_PAYMENT_TOTALRENT_BRF' : 'LEASE_AGREEMENT.HELP_TEXT.RENT_PAYMENT_TOTALRENT'
      ),
      heatingPayment: this.fcf.createMandatory('LEASE_AGREEMENT.HEATING_PAYMENT', this.dataContext.heatingPayment),
      hotWaterPayment: this.fcf.createMandatory('LEASE_AGREEMENT.HOT_WATER_PAYMENT', this.dataContext.hotWaterPayment),
      coldWaterPayment: this.fcf.createMandatory('LEASE_AGREEMENT.COLD_WATER_PAYMENT', this.dataContext.coldWaterPayment),
      householdElectricityPayment: this.fcf.createMandatory(
        'LEASE_AGREEMENT.HOUSEHOLD_ELECTRICITY_PAYMENT',
        this.dataContext.householdElectricityPayment
      ),
      parkingElectricityPayment: this.fcf.createMandatory(
        'LEASE_AGREEMENT.PARKING_ELECTRICITY_PAYMENT',
        this.dataContext.parkingElectricityPayment
      ),
      parkingChargingElectricityPayment: this.fcf.createMandatory(
        'LEASE_AGREEMENT.PARKING_CHARGING_ELECTRICITY_PAYMENT',
        this.dataContext.parkingChargingElectricityPayment
      ),
      leaseAgreementNumber: this.fcf.createMandatory('LEASE_AGREEMENT.LEASE_AGREEMENT_NUMBER', this.dataContext.leaseAgreementNumber),
      popularName: this.fcf.create('LEASE_AGREEMENT.POPULAR_NAME', this.dataContext.popularName),
      leaseAgreementArea: this.fcf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_AREA', this.dataContext.leaseAgreementArea),
      rentalObjectSumSurface: this.fcf.createReadonly(
        'LEASE_AGREEMENT.LEASE_AGREEMENT_SUM_RENTAL_OBJECT_AREA',
        this.dataContext.rentalObjectSumSurface
      ),
      subCategory: this.fcf.create('LEASE_AGREEMENT.LEASE_AGREEMENT_SUB_CATEGORY', this.dataContext.subCategory),
      leaseAgreementRows: this.fb.group({}),
      originalContractualRent: this.fcf.create(
        this.isBrf() ? 'LEASE_AGREEMENT.ORIGINAL_CONTRACTUAL_RENT_BRF' : 'LEASE_AGREEMENT.ORIGINAL_CONTRACTUAL_RENT',
        this.dataContext.originalContractualRent,
        null,
        null,
        this.isBrf() ? 'LEASE_AGREEMENT.HELP_TEXT.ORIGINAL_CONTRACTUAL_RENT_BRF' : 'LEASE_AGREEMENT.HELP_TEXT.ORIGINAL_CONTRACTUAL_RENT'
      ),
      usage: this.fcf.create('LEASE_AGREEMENT.USAGE', this.dataContext.usage, null, null, 'LEASE_AGREEMENT.HELP_TEXT.USAGE'),
      specialTerms: this.fcf.createMaxLength('LEASE_AGREEMENT.SPECIAL_TERMS', this.dataContext.specialTerms, 3000),
      rentPaymentTerms: this.fcf.createMaxLength('LEASE_AGREEMENT.RENT_PAYMENT_TERMS', this.dataContext.rentPaymentTerms, 1000),
      note: this.fcf.createMaxLength('COMMON.NOTE', this.dataContext.note, 1000),
      individualInvoiceAddress: this.fcf.create('LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS', this.dataContext.individualInvoiceAddress),
      tenantOne: this.fcf.createMandatory('LEASE_AGREEMENT.TENANT_ONE', this.dataContext.tenantOne, 'TENANT.HELP_TEXT.NEW_TENANT'),
      tenantTwo: this.fcf.create('LEASE_AGREEMENT.TENANT_TWO', this.dataContext.tenantTwo, null, null, 'TENANT.HELP_TEXT.NEW_TENANT'),
      noticePeriod: this.fb.group({}),
      representative: this.fcf.create('representative', this.representativeFilter),
    });

    this.addRentalObjectFormData(form);
    return form;
  }

  protected addRentalObjectFormData(form: FormGroup): void {
    form.addControl(
      'rentalObject',
      this.fcf.createWithOptions(this.isBrf() ? 'RENTAL_OBJECT.OBJECT' : 'LEASE_AGREEMENT.RENTAL_OBJECT', this.dataContext.rentalObject, {
        required: true,
        noSubmit: true,
      })
    );
  }

  protected isBrf(): boolean {
    return false;
  }

  private _getPath(category: LeaseAgreementCategory) {
    let segment = '';
    switch (category) {
      case LeaseAgreementCategory.Block:
      case LeaseAgreementCategory.Dwelling:
      case LeaseAgreementCategory.Parking:
      case LeaseAgreementCategory.Premises:
        segment = LeaseAgreementCategory[category].toLowerCase();
        break;

      case LeaseAgreementCategory.Other:
        segment = 'other-rental-object';
        break;

      case LeaseAgreementCategory.CooperativeHousing:
        segment = 'co-housing';
        break;

      default:
        throw new Error(`The specified contract category is not supported. Category: ${category}`);
    }
    return segment;
  }

  private _getPathForRentalObject(category: LeaseAgreementCategory) {
    if (category === LeaseAgreementCategory.CooperativeHousing) {
      return this._getPath(this.getLeaseAgreementCategory());
    }
    return this._getPath(category);
  }
  protected getLeaseAgreementCategory(): LeaseAgreementCategory {
    return this.dataContext.category;
  }

  private _getLeaseAgreementContractPath(category: LeaseAgreementCategory) {
    return this._getPath(category);
  }

  protected getBlockContractRentalObjectPath(category: LeaseAgreementCategory) {
    return this._getPath(category);
  }

  protected getRentalObjectCaption(): string {
    return 'LEASE_AGREEMENT.RENTAL_OBJECT';
  }

  onOpenRentalObjectClicked(): void {
    this.navigation.navigate([
      'my-property',
      this._getPathForRentalObject(this.getLeaseAgreementCategory()),
      'edit',
      this.dataContext.rentalObjectId,
    ]);
  }

  onFaultNotificationRowClicked(e: { context: FaultNotification; event: any }): void {
    this.navigation.navigate(['fault-notification', 'notifications', 'edit', e.context.id], null, e.event.ctrlKey);
  }

  onOtherLeaseAgreementClicked(e: { context: Contract; event: any }): void {
    this.navigation.navigate(
      [this._getLeaseAgreementContractPath(e.context.category), 'contracts', 'edit', e.context.id],
      null,
      e.event.ctrlKey
    );
  }

  currentNumber: string;

  individualAddressMessage: string;

  get titleContextInfo(): string {
    if (!this.dataContext?.id) return null;

    const rentalObject = this.dataContext.rentalObject || '';
    const rentalIdPrefix = `${this.dataContext.rentalId} `;
    const rentalObjectShort = this.dataContext.rentalId && rentalObject.startsWith(rentalIdPrefix)
      ? rentalObject.slice(rentalIdPrefix.length)
      : rentalObject;
    return [
      this.dataContext.leaseAgreementNumber,
      rentalObjectShort,
      this.languageService.instant(this.dataContext.statusCaption),
    ].filter(Boolean).join(', ');
  }

  protected init(data: TModel): void {
    this.addDeleteMenu = false;
    super.init(data);

    this.invoiceAddress = this.dataContext.invoiceAddress;
    this.fromDate = data.noticePeriod.from;
    this.rentalObjectSurface = this.dataContext.rentalObjectSurface;
    this.leaseAgreementRows = this.dataContext.leaseAgreementRows;
    this.individualAddressMessage = this.dataContext.individualInvoiceAddress
      ? 'LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS_ENABLED_INFO'
      : 'LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS_DISABLED_INFO';

    this._updateRentFields(this.dataContext.rentPayment);
    this.AddSubscription(
      this.form.controls['rentPayment'].valueChanges.subscribe(value => {
        this._updateRentFields(value);
      })
    );

    this.AddSubscription(this.messageDispatcher.messages.subscribe(message => this.onMessageRecieved(message)));

    this.AddSubscription(
      this.form.controls['individualInvoiceAddress'].valueChanges.subscribe(active => {
        this.individualAddressMessage = active
          ? 'LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS_ENABLED_INFO'
          : 'LEASE_AGREEMENT.INDIVIDUAL_INVOICE_ADDRESS_DISABLED_INFO';
      })
    );

    this.AddSubscription(
      this.form.controls['leaseAgreementNumber'].valueChanges.subscribe(value => {
        if (!this.form.controls['popularName'].value || this.form.controls['popularName'].value === this.currentNumber) {
          this.currentNumber = value;
          this.form.controls['popularName'].setValue(value);
        }
      })
    );

    if (this.form.contains('rentalObject')) {
      this.AddSubscription(this.form.controls['rentalObject'].valueChanges.subscribe(value => this.onRentalObjectChanged(value)));
    }
  }

  protected prepareSave(): TModel {
    const model = super.prepareSave();
    model.tenantOneName = this.dataContext.tenantOneName;
    model.tenantTwoName = this.dataContext.tenantTwoName;
    model.tenantOneContactIdentification = this.dataContext.tenantOneContactIdentification;
    model.tenantTwoContactIdentification = this.dataContext.tenantTwoContactIdentification;
    model.tenantOnePhone = this.dataContext.tenantOnePhone;
    model.tenantTwoPhone = this.dataContext.tenantTwoPhone;
    model.tenantOneEmail = this.dataContext.tenantOneEmail;
    model.tenantTwoEmail = this.dataContext.tenantTwoEmail;
    model.eSignatureActive = this.dataContext.eSignatureActive;
    model.leaseAgreementRows = this.leaseAgreementRows.filter(row => row.article);
    model.leaseAgreementRows.forEach(row => this.AdjustToDate(row));
    model.rentalObjects = this.dataContext.rentalObjects;
    model.invoicePayment = model.invoicePayment ? 1 : 0;
    model.representatives = this.dataContext.representatives;
    return model;
  }
  AdjustToDate(row: LeaseRow): void {
    if (row.voided || row.toDate < row.fromDate) {
      row.toDate = LocalDate.fromDate(row.fromDate.clone().addDays(-1));
    }
  }

  onNoticePeriodFromChanged(value: LocalDate) {
    this.fromDate = value;
  }

  protected setAddress(address: PostalAddress) {
    if (!address) return;
    let adr = this.form.controls['invoiceAddress'] as FormGroup;
    if (adr !== undefined && adr.controls['city'] !== undefined) {
      adr.controls['city'].setValue(address.city);
      adr.controls['postalCode'].setValue(address.postalCode);
      adr.controls['street'].setValue(address.street);
      adr.controls['country'].setValue(address.country);
    }
  }

  conditionsForm: FormGroup;
  onRentalObjectChanged(rentalObject: VacantRentalObject) {
    if (rentalObject && rentalObject.id) {
      this.form.controls[formModelNames.leaseAgreementNumber].setValue(rentalObject.leaseAgreementNumber);
      this.dataContext.company = rentalObject.company;
      this.dataContext.currencyCode = rentalObject.currencyCode;
      this.dataContext.propertyId = rentalObject.property;
      let connection = new LeaseAgreementRentalObject();
      connection.rentalObjectId = rentalObject.rentalObjectId;
      this.dataContext.rentalObjects = [connection];
      this.dataContext.rentalObjectSurface = rentalObject.surface;
      this.dataContext.currentPeriodText = rentalObject.currentPeriodText;
      this.dataContext.noticePeriod.landlordDuration = rentalObject.leaseAgreementTemplate.noticePeriodLandlord;
      this.dataContext.noticePeriod.tenantDuration = rentalObject.leaseAgreementTemplate.noticePeriodTenant;
      this.dataContext.earlyTerminationIsUsed = rentalObject.earlyTerminationIsUsed;
      this.dataContext.noticePeriod.allowEarlyTermination = rentalObject.allowEarlyTerminationByDefault;
      this.noticePeriodComponent?.setEarlyTerminationConfiguration(
        rentalObject.earlyTerminationIsUsed,
        rentalObject.allowEarlyTerminationByDefault
      );
      this.dataContext.invoicePeriod = rentalObject.leaseAgreementTemplate.invoicePeriodMonths;
      this.dataContext.specialTerms = rentalObject.standardSpecialTerms;
      this.form.controls['specialTerms'].setValue(rentalObject.standardSpecialTerms);
      this.dataContext.blockingWarning = rentalObject.blockingWarning;
      this.dataContext.rentPaymentTerms = rentalObject.standardRentPaymentTerms;
      this.form.controls['rentPaymentTerms'].setValue(rentalObject.standardRentPaymentTerms);
      if (rentalObject.standardRentPaymentTerms && rentalObject.standardRentPaymentTerms.length > 0) {
        this.form.controls['rentPayment'].setValue(RentPaymentKind.OwnTemplate);
      } else {
        if (
          rentalObject.leaseAgreementTemplate.coldWaterPayment == ColdWaterPaymentKind.IncludedInTheRent &&
          rentalObject.leaseAgreementTemplate.heatingPayment == HeatingPaymentKind.IncludedInTheRent &&
          rentalObject.leaseAgreementTemplate.householdElectricityPayment == HouseholdElectricityPaymentKind.IncludedInTheRent &&
          rentalObject.leaseAgreementTemplate.hotWaterPayment == HotWaterPaymentKind.IncludedInTheRent &&
          rentalObject.leaseAgreementTemplate.parkingElectricityPayment == ParkingElectricityPaymentKind.IncludedInTheRent &&
          rentalObject.leaseAgreementTemplate.parkingChargingElectricityPayment == ParkingElectricityPaymentKind.IncludedInTheRent
        )
          this.form.controls['rentPayment'].setValue(RentPaymentKind.TotalRent);
        else this.form.controls['rentPayment'].setValue(RentPaymentKind.SpecifiedAdditions);
      }
      this.form.controls['coldWaterPayment'].setValue(rentalObject.leaseAgreementTemplate.coldWaterPayment);
      this.form.controls['hotWaterPayment'].setValue(rentalObject.leaseAgreementTemplate.hotWaterPayment);
      this.form.controls['householdElectricityPayment'].setValue(rentalObject.leaseAgreementTemplate.householdElectricityPayment);
      this.form.controls['heatingPayment'].setValue(rentalObject.leaseAgreementTemplate.heatingPayment);
      this.form.controls['parkingElectricityPayment'].setValue(rentalObject.leaseAgreementTemplate.parkingElectricityPayment);
      this.form.controls['parkingChargingElectricityPayment'].setValue(rentalObject.leaseAgreementTemplate.parkingChargingElectricityPayment);

      if (rentalObject.category === RentalObjectCategory.Premises) {
        this.conditionsForm = this.fb.group({
          propertyTaxPayer: this.fcf.create(
            'LEASE_AGREEMENT.PREMISES_LEASE.PROPERTY_TAX_RESPONSIBLE',
            rentalObject.leaseAgreementTemplate?.propertyTaxPaidBy
          ),
          propertyTaxCalculationBasis: this.fcf.create(
            'PROPERTY_TAX.CALCULATION_BASIS',
            rentalObject.leaseAgreementTemplate?.propertyTaxCalculationBasis
          ),
          propertyTaxPercentage: this.fcf.create(
            'PROPERTY_TAX.PERCENTAGE_TEXT',
            toPropertyTaxPercentageDisplayValue(rentalObject.leaseAgreementTemplate?.propertyTaxPercentage)
          ),
          propertyTaxCalculationMethod: this.fcf.create(
            'PROPERTY_TAX.CALCULATION_METHOD',
            rentalObject.leaseAgreementTemplate?.propertyTaxCalculationMethod
          ),
          propertyTaxAssessmentUnitType: this.fcf.createReadonly(
            'PROPERTY_TAX.CALCULATION_BASIS_BY_TAXTYPECODE',
            this.getpropertyTaxAssessmentUnitTypeString(rentalObject.leaseAgreementTemplate)
          ),
          propertyTaxArticlesNonRetroactive: this.fcf.create(
            'PROPERTY_TAX.NON_RETROACTIVE_ARTICLE',
            rentalObject.leaseAgreementTemplate?.propertyTaxArticleId
          ),
          propertyTaxArticlesRetroactive: this.fcf.create(
            'PROPERTY_TAX.RETROACTIVE_ARTICLE',
            rentalObject.leaseAgreementTemplate?.propertyTaxRetroArticleId
          ),
        });
        this.form.addControl('agreementConditions', this.conditionsForm);
        const ctrl = this.conditionsForm.get('propertyTaxAssessmentUnitType');
        ctrl?.disable({ emitEvent: false });
        this.populatePropertyTaxArticles(rentalObject);
      }

      this.setObjectCategory(rentalObject.category);

      this.dataContext.negotiatingClause = rentalObject.hasNegotiationProcedure;
      if (this.dataContext.negotiatingClause) this.dataContext.organizationOfTenants = OrganizationOfTenantsKind.TenantsAssociation;

      let newAddress = new PostalAddress();
      if (
        rentalObject.address &&
        (this.dataContext.category === LeaseAgreementCategory.Dwelling ||
          this.dataContext.category === LeaseAgreementCategory.CooperativeHousing)
      ) {
        newAddress = <PostalAddress>Object.assign(new PostalAddress(), rentalObject.address);
        newAddress.additionalName = this.invoiceAddress.additionalName;
        newAddress.name = this.invoiceAddress.name;
      } else if (this.invoiceAddress) {
        newAddress = <PostalAddress>Object.assign(new PostalAddress(), this.invoiceAddress);
      }
      this.setAddress(newAddress);
      this.invoiceAddress = newAddress;

      this.dataContext.leaseAgreementRows = [];

      if (rentalObject.vacantFrom) {
        this.fromDate = rentalObject.vacantFrom;
        this.dataContext.fromDate = rentalObject.vacantFrom;
      } else {
        let date = LocalDate.today();
        date.addMonths(1);
        this.fromDate = LocalDate.fromDate(date.firstDayInMonth());
      }
      if (this.noticePeriodComponent) {
        this.noticePeriodComponent.from = this.fromDate;
        this.noticePeriodComponent.landlordDuration.setValue(rentalObject.leaseAgreementTemplate.noticePeriodLandlord);
        this.noticePeriodComponent.tenantDuration.setValue(rentalObject.leaseAgreementTemplate.noticePeriodTenant);
      }
      if (this.negotiationComponent) {
        this.negotiationComponent.negotiatingClause.setValue(rentalObject.hasNegotiationProcedure);
        if (rentalObject.hasNegotiationProcedure)
          this.negotiationComponent.organizationOfTenants.setValue(OrganizationOfTenantsKind.TenantsAssociation);
        else this.negotiationComponent.organizationOfTenants.setValue(OrganizationOfTenantsKind.None);
      }

      if (this.form.contains('invoicePeriod')) {
        this.form.controls['invoicePeriod'].setValue(rentalObject.leaseAgreementTemplate.invoicePeriodMonths);
      }
      let originalContractualRent = new Money(rentalObject.currencyCode, 0);
      if (rentalObject.templates) {
        this.leaseAgreementRows = [];
        for (let i = 0; i < rentalObject.templates.length; i++) {
          let emptyTodate = rentalObject.templates[i].toDate === undefined;
          emptyTodate = emptyTodate || !rentalObject.templates[i].toDate;
          emptyTodate = emptyTodate || rentalObject.templates[i].toDate === null;
          emptyTodate = emptyTodate || rentalObject.templates[i].toDate === null || rentalObject.templates[i].toDate.year === null;
          if (emptyTodate) {
            let leaseRow = Object.assign(new LeaseRow(rentalObject.currencyCode), rentalObject.templates[i]);
            if (LocalDate.GreaterThan(this.fromDate, leaseRow.fromDate)) {
              leaseRow.fromDate = this.fromDate;
            }
            this.leaseAgreementRows.push(leaseRow);
            if (leaseRow.article.includeInContractualRent) {
              if (!LocalDate.GreaterThan(leaseRow.fromDate, this.fromDate)) {
                originalContractualRent = new Money(
                  originalContractualRent.currencyCode,
                  (originalContractualRent.amount += leaseRow.yearRent.amount)
                );
              }
            }
          } else {
            if (
              LocalDate.GreaterThan(rentalObject.templates[i].toDate, this.fromDate) ||
              LocalDate.Equal(rentalObject.templates[i].toDate, this.fromDate)
            ) {
              let leaseRow = Object.assign(new LeaseRow(rentalObject.currencyCode), rentalObject.templates[i]);
              if (LocalDate.GreaterThan(this.fromDate, leaseRow.fromDate)) {
                leaseRow.fromDate = this.fromDate;
              }
              this.leaseAgreementRows.push(leaseRow);
              if (leaseRow.article.includeInContractualRent) {
                if (!LocalDate.GreaterThan(leaseRow.fromDate, this.fromDate)) {
                  originalContractualRent = new Money(
                    originalContractualRent.currencyCode,
                    (originalContractualRent.amount += leaseRow.yearRent.amount)
                  );
                }
              }
            }
          }
        }
        this.dataContext.originalContractualRent = originalContractualRent;
        this.form.controls[formModelNames.originalContractualRent].setValue(originalContractualRent);
      }
    }
  }
  protected setObjectCategory(category: RentalObjectCategory) {}

  onTenantOneChange(tenant: Tenant) {
    this.tenantOne = tenant;
  }

  imdConsumption: LeaseAgreementImdConsumption[] = [];
  logs: LogRecord[] = [];

  override initializeSection(section: string) {
    switch (section) {
      case 'logs':
        this.AddSubscription(
          this.logService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
            this.logs = res.items;
          })
        );

      case 'imdConsumption':
        this.AddSubscription(
          this.leaseAgreementImdConsumptionService.getPagedList(this.dataContext.id, 1, 500).subscribe(res => {
            this.imdConsumption = res.items;
          })
        );
    }
    super.initializeSection(section);
  }

  onLeaseRowsChange(newRows: LeaseRow[]) {
    this.updateContractualRent(newRows);
  }

  updateContractualRent(newRows: LeaseRow[]) {
    if (this.isNew) {
      let originalContractualRent = new Money(this.dataContext.currencyCode, 0);
      for (let i = 0; i < newRows.length; i++) {
        let row = newRows[i];
        let emptyTodate = row.toDate === undefined;
        emptyTodate = emptyTodate || !row.toDate;
        emptyTodate = emptyTodate || row.toDate === null || row.toDate.year === null;
        if (emptyTodate) {
          if (row.article.includeInContractualRent) {
            if (!LocalDate.GreaterThan(row.fromDate, this.fromDate)) {
              originalContractualRent = new Money(
                originalContractualRent.currencyCode,
                (originalContractualRent.amount += row.yearRent.amount)
              );
            }
          }
        } else {
          if (LocalDate.GreaterThan(row.toDate, this.fromDate) || LocalDate.Equal(row.toDate, this.fromDate)) {
            if (row.article.includeInContractualRent) {
              if (!LocalDate.GreaterThan(row.fromDate, this.fromDate)) {
                originalContractualRent = new Money(
                  originalContractualRent.currencyCode,
                  (originalContractualRent.amount += row.yearRent.amount)
                );
              }
            }
          }
        }
      }
      this.form.controls[formModelNames.originalContractualRent].setValue(originalContractualRent);
    }
  }

  onItemSourceChange(newRows: LeaseRow[]) {
    this.leaseAgreementRows = newRows;
    this.updateContractualRent(this.leaseAgreementRows);
  }

  getpropertyTaxAssessmentUnitTypeString(leaseAgreementTemplate: LeaseAgreementTemplate): string {
    const src = leaseAgreementTemplate?.propertyTaxAssessmentUnitType;
    if (!src) return this.languageService.instant('PROPERTY_TAX.NO_ASSESSMENT_UNIT');

    const entry =
      src instanceof Map
        ? Array.from(src.entries())[0]
        : Object.keys(src as any).length
          ? [Number(Object.keys(src as any)[0]), (src as any)[Object.keys(src as any)[0]]]
          : null;

    if (!entry || !Number.isFinite(entry[0]) || !entry[1]) {
      return this.languageService.instant('PROPERTY_TAX.NO_ASSESSMENT_UNIT');
    }
    const [code, type] = entry as [number, TaxTypeCode];
    return `${code}, ${type.name}`;
  }

  propertyTaxArticlesNonRetroactive: SelectionItem[] = [];
  propertyTaxArticlesRetroactive: SelectionItem[] = [];

  private populatePropertyTaxArticles(rentalObject: VacantRentalObject): void {
    const articles = (rentalObject?.leaseAgreementTemplate?.propertyTaxArticles ?? []).map(a =>
      a instanceof Article ? a : new Article(a)
    );

    this.propertyTaxArticlesNonRetroactive = articles
      .filter(x => !x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    this.propertyTaxArticlesRetroactive = articles
      .filter(x => x.isRetroactiveForPropertyTax)
      .map(x => new SelectionItem(x.toString(), x.id));

    // Keep form controls in sync without emitting change events.
    const nonRetroId = rentalObject?.leaseAgreementTemplate?.propertyTaxArticleId ?? null;
    const retroId = rentalObject?.leaseAgreementTemplate?.propertyTaxRetroArticleId ?? null;

    // Only set ids that exist in the current option set to avoid invalid values.
    const hasNonRetro = this.propertyTaxArticlesNonRetroactive.some(x => x.value === nonRetroId);
    const hasRetro = this.propertyTaxArticlesRetroactive.some(x => x.value === retroId);

    this.conditionsForm?.get('propertyTaxArticlesNonRetroactive')?.setValue(hasNonRetro ? nonRetroId : null, { emitEvent: false });
    this.conditionsForm?.get('propertyTaxArticlesRetroactive')?.setValue(hasRetro ? retroId : null, { emitEvent: false });
  }
}
